import type {
  ExpansionPackDefinition,
  GrowthPackDefinition,
  IndustryCatalogValidationResult,
  IndustryPackDefinition,
  IndustryValidationIssue,
} from './types';

function issue(
  packId: IndustryPackDefinition['id'] | 'catalog',
  code: string,
  message: string,
  ref?: string,
): IndustryValidationIssue {
  return { packId, code, message, ref };
}

function collectIds<T extends { id: string }>(items: T[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) {
    counts.set(item.id, (counts.get(item.id) ?? 0) + 1);
  }
  return counts;
}

function validateGrowthPack(
  pack: IndustryPackDefinition,
  growth: GrowthPackDefinition,
  missionIds: Set<string>,
  productIds: Set<string>,
  scenarioIds: Set<string>,
  growthIds: Set<string>,
): IndustryValidationIssue[] {
  const issues: IndustryValidationIssue[] = [];

  if (growth.industryId !== pack.id) {
    issues.push(
      issue(pack.id, 'GROWTH_INDUSTRY_MISMATCH', `Growth pack ${growth.id} industry mismatch`, growth.id),
    );
  }
  if (growthIds.has(growth.id)) {
    issues.push(issue(pack.id, 'DUPLICATE_GROWTH_ID', `Duplicate growth pack id`, growth.id));
  }
  growthIds.add(growth.id);

  for (const mid of growth.unlocks.missionIds ?? []) {
    if (!missionIds.has(mid)) {
      issues.push(issue(pack.id, 'UNKNOWN_MISSION_REF', `Growth unlock references unknown mission`, mid));
    }
  }
  for (const pid of growth.unlocks.productIds ?? []) {
    if (!productIds.has(pid)) {
      issues.push(issue(pack.id, 'UNKNOWN_PRODUCT_REF', `Growth unlock references unknown product`, pid));
    }
  }
  for (const sid of growth.unlocks.scenarioIds ?? []) {
    if (!scenarioIds.has(sid)) {
      issues.push(issue(pack.id, 'UNKNOWN_SCENARIO_REF', `Growth unlock references unknown scenario`, sid));
    }
  }
  for (const mid of growth.unlockConditions?.completedMissionIds ?? []) {
    if (!missionIds.has(mid)) {
      issues.push(issue(pack.id, 'UNKNOWN_MISSION_PREREQ', `Growth condition references unknown mission`, mid));
    }
  }
  for (const sid of growth.unlockConditions?.completedScenarioIds ?? []) {
    if (!scenarioIds.has(sid)) {
      issues.push(issue(pack.id, 'UNKNOWN_SCENARIO_PREREQ', `Growth condition references unknown scenario`, sid));
    }
  }

  return issues;
}

function validateExpansionPack(
  pack: IndustryPackDefinition,
  expansion: ExpansionPackDefinition,
  growthIds: Set<string>,
  expansionIds: Set<string>,
): IndustryValidationIssue[] {
  const issues: IndustryValidationIssue[] = [];

  if (expansion.industryId !== pack.id) {
    issues.push(
      issue(pack.id, 'EXPANSION_INDUSTRY_MISMATCH', `Expansion pack industry mismatch`, expansion.id),
    );
  }
  if (expansionIds.has(expansion.id)) {
    issues.push(issue('catalog', 'DUPLICATE_EXPANSION_ID', `Duplicate expansion id globally`, expansion.id));
  }
  expansionIds.add(expansion.id);

  if (expansion.requiresGrowthTierId && !growthIds.has(expansion.requiresGrowthTierId)) {
    issues.push(
      issue(pack.id, 'UNKNOWN_GROWTH_REQUIREMENT', `Expansion requires unknown growth tier`, expansion.id),
    );
  }
  for (const req of expansion.requiresExpansionIds ?? []) {
    if (!expansionIds.has(req) && !pack.expansionPacks.some((e) => e.id === req)) {
      // defer cross-ref until all expansions registered — checked in second pass
    }
  }

  const addMissionIds = collectIds(expansion.adds.missions ?? []);
  for (const [id, count] of addMissionIds) {
    if (count > 1) {
      issues.push(issue(pack.id, 'DUPLICATE_MISSION_ID', `Duplicate mission in expansion`, id));
    }
  }

  return issues;
}

export function validateIndustryPack(pack: IndustryPackDefinition): IndustryValidationIssue[] {
  const issues: IndustryValidationIssue[] = [];

  if (pack.id !== pack.category.id) {
    issues.push(issue(pack.id, 'PACK_CATEGORY_MISMATCH', 'Pack id must match category.id'));
  }

  const scenarioIds = collectIds(pack.scenarios);
  const productIds = collectIds(pack.products);
  const missionIds = collectIds(pack.missions);

  for (const [id, count] of scenarioIds) {
    if (count > 1) issues.push(issue(pack.id, 'DUPLICATE_SCENARIO_ID', 'Duplicate scenario id', id));
    if (pack.scenarios.find((s) => s.id === id)?.category !== pack.id && pack.id !== 'sandbox') {
      issues.push(issue(pack.id, 'SCENARIO_CATEGORY_MISMATCH', 'Scenario category mismatch', id));
    }
  }
  for (const [id, count] of productIds) {
    if (count > 1) issues.push(issue(pack.id, 'DUPLICATE_PRODUCT_ID', 'Duplicate product id', id));
  }
  for (const [id, count] of missionIds) {
    if (count > 1) issues.push(issue(pack.id, 'DUPLICATE_MISSION_ID', 'Duplicate mission id', id));
  }

  for (const mission of pack.missions) {
    for (const prereq of mission.prerequisites ?? []) {
      if (!missionIds.has(prereq)) {
        issues.push(issue(pack.id, 'UNKNOWN_MISSION_PREREQ', 'Mission prerequisite missing', prereq));
      }
    }
    for (const unlock of mission.rewards.unlocks ?? []) {
      if (!productIds.has(unlock)) {
        issues.push(issue(pack.id, 'UNKNOWN_REWARD_UNLOCK', 'Mission reward unlock missing', unlock));
      }
    }
  }

  const growthIds = new Set<string>();
  const allMissionIds = new Set(missionIds.keys());
  const allProductIds = new Set(productIds.keys());
  const allScenarioIds = new Set(scenarioIds.keys());

  for (const expansion of pack.expansionPacks) {
    for (const m of expansion.adds.missions ?? []) allMissionIds.add(m.id);
    for (const p of expansion.adds.products ?? []) allProductIds.add(p.id);
    for (const s of expansion.adds.scenarios ?? []) allScenarioIds.add(s.id);
  }

  const tiers = pack.growthPacks.map((g) => g.tier).sort((a, b) => a - b);
  if (tiers.length > 0 && tiers[0] !== 1) {
    issues.push(issue(pack.id, 'GROWTH_TIER_START', 'Growth tiers must start at tier 1'));
  }

  for (const growth of pack.growthPacks) {
    issues.push(
      ...validateGrowthPack(
        pack,
        growth,
        allMissionIds,
        allProductIds,
        allScenarioIds,
        growthIds,
      ),
    );
  }

  const expansionIds = new Set<string>();
  for (const expansion of pack.expansionPacks) {
    issues.push(...validateExpansionPack(pack, expansion, growthIds, expansionIds));
  }
  for (const expansion of pack.expansionPacks) {
    for (const req of expansion.requiresExpansionIds ?? []) {
      if (!expansionIds.has(req)) {
        issues.push(
          issue(pack.id, 'UNKNOWN_EXPANSION_REQUIREMENT', 'Expansion requires missing pack', req),
        );
      }
    }
  }

  return issues;
}

export function validateIndustryCatalog(
  packs: IndustryPackDefinition[],
): IndustryCatalogValidationResult {
  const issues: IndustryValidationIssue[] = [];
  const packIds = new Set<string>();
  let expansionCount = 0;
  let growthTierCount = 0;

  for (const pack of packs) {
    if (packIds.has(pack.id)) {
      issues.push(issue('catalog', 'DUPLICATE_PACK_ID', 'Duplicate industry pack id', pack.id));
    }
    packIds.add(pack.id);
    expansionCount += pack.expansionPacks.length;
    growthTierCount += pack.growthPacks.length;
    issues.push(...validateIndustryPack(pack));
  }

  return {
    valid: issues.length === 0,
    issues,
    packCount: packs.length,
    expansionCount,
    growthTierCount,
  };
}